/**
 * @file InfoTip.tsx
 * @brief A small (i) that explains the word next to it, in the meeting
 *        scores' style (MeetingScoreStrip MetricInfo).
 *
 * @details
 * Hover opens it on the Mac and web; a tap or click toggles it, so it works
 * on a touch screen; a tap outside or Escape closes it.
 *
 * @author DES
 * @date 21-10-2026
 */

import { useRef, useState } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/Tooltip";
import { cn } from "@/shared/theme/cn";

export function InfoTip({ label, text, className }: { label: string; text: string; className?: string }) {
  const [open, setOpen] = useState(false);
  // Open-state at pointer down; null for a keyboard click.
  const wasOpen = useRef<boolean | null>(null);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`About ${label}`}
            onPointerDownCapture={() => {
              // Radix closes an open tip on pointer down, before the click.
              wasOpen.current = open;
            }}
            onClick={(e) => {
              // Radix closes a tooltip on trigger click; a click toggles here,
              // so a second tap closes it.
              e.preventDefault();
              e.stopPropagation();
              setOpen(!(wasOpen.current ?? open));
              wasOpen.current = null;
            }}
            className={cn(
              "inline-flex size-4 shrink-0 items-center justify-center rounded-full align-middle text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
              className,
            )}
          >
            <Info className="size-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] whitespace-normal normal-case leading-snug tracking-normal">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
